/**
 * Route parsing logic for AI agent context.
 *
 * Extracts page type, entity, and metadata from the current URL pathname.
 */

import type {
  EntityType,
  PageType,
  ParsedContext,
} from "./agent-context-types";

// Regex patterns (top-level for performance)
const TERMINAL_READERS_REGEX = /readers\.([^.]+)/;

// =============================================================================
// ROUTE PARSERS - Split into focused functions for reduced cognitive complexity
// =============================================================================

/** Parse location sub-pages and return page type, entity, and metadata */
function parseLocationSubPage(
  subPage: string,
  entityId: string | undefined
): {
  pageType: PageType;
  entity?: ParsedContext["entity"];
  metadata?: ParsedContext["metadata"];
} {
  const subPageMap: Record<
    string,
    {
      pageType: PageType;
      entityType?: EntityType;
      metadata?: ParsedContext["metadata"];
    }
  > = {
    receipts: { pageType: "receipts" },
    discounts: { pageType: "discounts", entityType: "discount" },
    devices: { pageType: "settings", entityType: "device" },
    profile: { pageType: "settings" },
    "end-of-day": {
      pageType: "reports",
      metadata: { reportType: "end-of-day" },
    },
    tips: { pageType: "reports", metadata: { reportType: "tips" } },
    "voids-and-refunds": { pageType: "orders" },
    stations: { pageType: "settings" },
    shifts: { pageType: "employees", entityType: "shift" },
  };

  const config = subPageMap[subPage];
  if (!config) {
    return { pageType: "location" };
  }

  const entity =
    config.entityType && entityId
      ? { type: config.entityType, id: entityId }
      : undefined;

  return { pageType: config.pageType, entity, metadata: config.metadata };
}

/** Parse location routes */
function parseLocationRoute(segments: string[]): Partial<ParsedContext> {
  if (!segments[1]) {
    return { pageType: "location" };
  }

  const locationId = segments[1];
  const baseEntity: ParsedContext["entity"] = {
    type: "location",
    id: locationId,
  };

  if (!segments[2]) {
    return { pageType: "location", entity: baseEntity, locationId };
  }

  const { pageType, entity, metadata } = parseLocationSubPage(
    segments[2],
    segments[3]
  );
  return {
    pageType,
    entity: entity ?? baseEntity,
    locationId,
    metadata,
  };
}

/** Parse menu routes */
function parseMenuRoute(segments: string[]): Partial<ParsedContext> {
  if (!segments[1]) {
    return { pageType: "menu" };
  }

  const menuId = segments[1];
  const subPage = segments[2];
  const entityId = segments[3];

  const menuSubPageMap: Record<string, EntityType> = {
    items: "item",
    categories: "category",
    modifiers: "modifier",
  };

  const entityType = subPage && entityId ? menuSubPageMap[subPage] : undefined;
  const entity: ParsedContext["entity"] =
    entityType && entityId
      ? { type: entityType, id: entityId }
      : { type: "menu", id: menuId };

  return { pageType: "menu", entity };
}

/** Parse employee routes */
function parseEmployeeRoute(segments: string[]): Partial<ParsedContext> {
  if (!segments[1]) {
    return { pageType: "employees" };
  }

  const subSegment = segments[1];
  if (subSegment === "shifts") {
    const entity = segments[2]
      ? { type: "shift" as EntityType, id: segments[2] }
      : undefined;
    return { pageType: "employees", entity };
  }

  if (subSegment !== "new" && subSegment !== "manage-roles") {
    return {
      pageType: "employees",
      entity: { type: "employee", id: subSegment },
    };
  }

  return { pageType: "employees" };
}

/** Parse settings routes */
function parseSettingsRoute(segments: string[]): Partial<ParsedContext> {
  if (!segments[1]?.startsWith("terminals")) {
    return { pageType: "settings" };
  }

  const readerMatch = TERMINAL_READERS_REGEX.exec(segments[1]);
  const entity = readerMatch?.[1]
    ? { type: "device" as EntityType, id: readerMatch[1] }
    : undefined;

  return { pageType: "settings", entity };
}

/**
 * Parse the current route to extract page type, entity, and metadata
 *
 * Complete route coverage for V4 context awareness.
 */
export function parseRoute(pathname: string): ParsedContext {
  const segments = pathname.split("/").filter(Boolean);
  const cleanSegments = segments.filter((s) => s !== "_authenticated");

  if (cleanSegments.length === 0 || cleanSegments[0] === "") {
    return { pageType: "dashboard", currentPage: pathname };
  }

  const firstSegment = cleanSegments[0] ?? "";

  // Route-specific parsers
  const routeParsers: Record<string, () => Partial<ParsedContext>> = {
    locations: () => parseLocationRoute(cleanSegments),
    menus: () => parseMenuRoute(cleanSegments),
    employees: () => parseEmployeeRoute(cleanSegments),
    reports: () => ({
      pageType: "reports",
      metadata: cleanSegments[1] ? { reportType: cleanSegments[1] } : undefined,
    }),
    payments: () => ({
      pageType: "payments",
      entity: cleanSegments[1]
        ? { type: "order", id: cleanSegments[1] }
        : undefined,
    }),
    payouts: () => ({ pageType: "payments" }),
    balances: () => ({ pageType: "payments" }),
    disputes: () => ({ pageType: "payments" }),
    analytics: () => ({
      pageType: "reports",
      metadata: { reportType: "analytics" },
    }),
    documents: () => ({ pageType: "settings" }),
    settings: () => parseSettingsRoute(cleanSegments),
    inventory: () => ({ pageType: "inventory" }),
  };

  const parser = routeParsers[firstSegment];
  const result = parser ? parser() : { pageType: "other" as PageType };

  return {
    pageType: result.pageType ?? "other",
    currentPage: pathname,
    entity: result.entity,
    locationId: result.locationId,
    metadata: result.metadata,
  };
}
